// MA-706 — `skip_count_line`: a horizontal number TRACK for the ms.math.16
// skip-counting skill's reliably-parseable "shown terms + one blank" shapes
// (skip_count_next/offset_next/missing_term/reverse — see
// contentToEngineItem.ts's MA-706 routing block and `parseSkipCountLine`).
//
// D1 (operator decision, MA-706 spec) — the shape:
//   - one STOP per SEQUENCE POSITION, not one tick per integer (a sequence
//     can reach 100; a per-integer number line would be absurdly wide).
//   - each SHOWN term is printed under its stop.
//   - the BLANK position's stop is drawn EMPTY — a dashed outline + "?" —
//     its value is NEVER printed (the answer numeral appears nowhere: not
//     in the markup, not in aria, not in a data-* attribute).
//   - equal arcs ("jumps") above every pair of consecutive stops, all the
//     same visual size (this is a POSITION track, not a magnitude-scaled
//     number line — the arc is decorative structure, not a measured hop).
//   - an arc between two SHOWN terms is labeled with the step ("+5"), but
//     ONLY when at least one such all-shown pair exists on this item (every
//     real corpus item has at least 2 shown terms, so this is always true
//     in practice; the guard exists for a hypothetical degenerate payload).
//     An arc touching the blank stop carries NO label — labeling it would
//     let a child derive the answer from "blank stop = neighbor ± step"
//     printed right next to the blank, which the mapper's fail-closed
//     parse already prevents from being wrong, but D1 keeps the LABEL
//     itself off that arc regardless, so the picture states the step only
//     where it is independently confirmable, never as an answer hint.
//
// ANSWER-SAFETY: `terms` never carries the blank stop's value — the mapper
// (`parseSkipCountLine`) only ever supplies `null` at that index. This
// component has no other source of that number (no `total`/`a`/`b` reads),
// so there is no code path by which it could leak — see
// answerVisibleInPicture.ts's `skip_count_line` contract (readable set =
// shown terms only, which by construction excludes the blank/answer for a
// strictly monotonic arithmetic sequence).
//
// D4 (layout): left-to-right, labels under stops with >= 14px clearance,
// viewBox reserves stroke width + the widest real-corpus label ("100").
// Max stops per item in the real corpus is 6 (<= the 8-stop cap the spec's
// Phase 0 STOP rule would have tripped past).
import type { MathRendererProps } from "./rendererRegistry";

const TRACK_COLOR = "var(--pal-orange, #d97742)";
const BORDER = "var(--color-border-secondary, #cdbfae)";
const TEXT = "var(--color-text-primary, #3d3228)";
const TEXT_SECONDARY = "var(--color-text-secondary, #7a6f60)";

const STOP_R = 24;
const PITCH = 84; // >= 2*STOP_R + room for a 3-digit label ("100") + gap
const PAD = 8;
const LABEL_GAP = 16; // >= D4's 14px clearance requirement
const LABEL_FONT_H = 22;
const ARC_H = 34; // arc rise above the stop centers
const ARC_LABEL_GAP = 6;
const ARC_LABEL_FONT_H = 18;

function SkipCountLineRenderer({ terms, step }: MathRendererProps) {
  const safeTerms: (number | null)[] =
    Array.isArray(terms) && terms.length >= 2
      ? terms.map((t) => (t === null ? null : Number.isFinite(t) ? (t as number) : null))
      : [null, null];
  const n = safeTerms.length;
  const safeStep = Number.isFinite(step) && (step as number) > 0 ? (step as number) : undefined;

  const stopY = ARC_H + ARC_LABEL_FONT_H + ARC_LABEL_GAP;
  const cx = (i: number) => PAD + STOP_R + i * PITCH;
  const trackW = PAD * 2 + STOP_R * 2 + (n - 1) * PITCH;
  const svgW = trackW;
  const svgH = stopY + STOP_R + LABEL_GAP + LABEL_FONT_H;

  const shownList = safeTerms.filter((t): t is number => t !== null).join(", ");
  // R2 fix (round-1 RED MF-2): direction comes from the PARSE, never a
  // fixed "+" assumption — the mapper's fail-closed parser already refuses
  // any item whose adjacent shown pairs disagree in sign, so any one
  // bothShown pair's sign IS the item's direction. Used for the aria
  // description only; each arc's own LABEL below is computed independently
  // per-pair (not from this single flag), which is what the picture-truth
  // pin actually reads.
  let isDescending = false;
  for (let i = 0; i < n - 1; i++) {
    const a = safeTerms[i];
    const b = safeTerms[i + 1];
    if (a !== null && b !== null) {
      isDescending = b - a < 0;
      break;
    }
  }
  const ariaLabel = isDescending
    ? `A number track, counting back, with jumps. Shown terms: ${shownList}.`
    : `A number track with jumps. Shown terms: ${shownList}.`;

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      width="100%"
      style={{ maxWidth: Math.min(420, svgW) }}
      role="img"
      aria-label={ariaLabel}
    >
      {/* Arcs (jumps) between every consecutive pair of stops.
          R1 fix (round-1 RED MF-1): each arc endpoint sits at the stop's
          EDGE — cx(i) + STOP_R on the left stop, cx(i+1) - STOP_R on the
          right stop — never at the stop's CENTRE. The quadratic control
          point stays at the true (unshifted) midpoint/apex, so the curve's
          tangent at each new endpoint still points away from that stop's
          circle (outward), which keeps the arc from re-entering it. This
          applies UNIFORMLY to every stop, filled or blank (dashed "?") —
          the arc never crosses into either circle, so the blank stop's "?"
          glyph is never touched, and a filled stop and a blank stop attach
          identically. Pin (skipCountVisualIntegrityTest.mjs, R1): for every
          arc, |endpoint.x - nearest stop cx| === STOP_R (+/-0.5). */}
      {Array.from({ length: n - 1 }).map((_, i) => {
        const x0 = cx(i);
        const x1 = cx(i + 1);
        const x0Edge = x0 + STOP_R;
        const x1Edge = x1 - STOP_R;
        const midX = (x0 + x1) / 2;
        const topY = stopY - ARC_H;
        const bothShown = safeTerms[i] !== null && safeTerms[i + 1] !== null;
        return (
          <g key={`arc-${i}`}>
            <path
              d={`M${x0Edge},${stopY} Q${midX},${topY} ${x1Edge},${stopY}`}
              stroke={TRACK_COLOR}
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
            />
            {bothShown && safeStep !== undefined && (() => {
              // R2 fix: this arc's own sign, read directly from ITS pair of
              // shown terms (never the item-level `isDescending` flag above,
              // and never a bare "+") — descending renders U+2212 MINUS,
              // never a hyphen.
              const pairDescending = (safeTerms[i + 1] as number) - (safeTerms[i] as number) < 0;
              const label = pairDescending ? `−${safeStep}` : `+${safeStep}`;
              return (
                <text
                  x={midX}
                  y={topY - 2}
                  textAnchor="middle"
                  fontSize={ARC_LABEL_FONT_H - 4}
                  fontWeight={600}
                  fill={TEXT_SECONDARY}
                >{label}</text>
              );
            })()}
          </g>
        );
      })}
      {/* Stops. */}
      {safeTerms.map((t, i) => {
        const x = cx(i);
        const isBlank = t === null;
        return (
          <g key={`stop-${i}`}>
            <circle
              cx={x}
              cy={stopY}
              r={STOP_R}
              fill={isBlank ? "none" : TRACK_COLOR}
              stroke={isBlank ? TEXT_SECONDARY : "none"}
              strokeWidth={isBlank ? 2.5 : 0}
              strokeDasharray={isBlank ? "5 4" : undefined}
            />
            {isBlank && (
              <text x={x} y={stopY + 7} textAnchor="middle" fontSize="22" fontWeight={700} fill={TEXT_SECONDARY}>
                ?
              </text>
            )}
            {!isBlank && (
              <text
                x={x}
                y={stopY + STOP_R + LABEL_GAP + 12}
                textAnchor="middle"
                fontSize="18"
                fontWeight={600}
                fill={TEXT}
              >
                {t}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export default SkipCountLineRenderer;
